import { show } from "@ebay/nice-modal-react";
import cx from "classnames";
import { useAtomValue } from "jotai";
import { canv } from "@/canv/global";
import { TemplatesWindow } from "@/interface/SideBar/TemplatesWindow";
import { BuyProModal } from "@/interface/ui/modals/BuyProModal";
import { Tooltip } from "@/interface/ui/Tooltip";
import { VStack } from "@/shared/ui/Stack";
import {
    arrowToolMenuVisibility,
    currentToolAtom,
    emoteToolMenuAtomVisibility,
    mindMapToolMenuVisibilityAtom,
    moreToolVisibility,
    penToolMenuVisibilityAtom,
    stickerToolMenuVisibility,
    templateToolMenuVisibility,
    toolBoxVisibility,
} from "../AppStore";
import { ArrowMenu } from "./ArrowMenu";
import { EmoteMenu } from "./EmoteMenu";
import { MindMapMenu } from "./MindMapMenu";
import { MoreWindow } from "./MoreWindow";
import { PenMenu } from "./PenMenu";
import styles from "./SideBar.module.css";
import { StickerMenu } from "./StickerMenu";
import { getToolsNames, isBuyProLockedTool } from "./utils";

export const SideBar = () => {
    const currentTool = useAtomValue(currentToolAtom);
    const isShowEmoteToolMenu = useAtomValue(emoteToolMenuAtomVisibility);
    const isShowArrowToolMenu = useAtomValue(arrowToolMenuVisibility);
    const isShowStickerToolMenu = useAtomValue(stickerToolMenuVisibility);
    const isShowMoreTool = useAtomValue(moreToolVisibility);
    const isShowToolBox = useAtomValue(toolBoxVisibility);
    const isShowPenToolMenu = useAtomValue(penToolMenuVisibilityAtom);
    const toolNames = getToolsNames();
    const isShowMindMapToolMenu = useAtomValue(mindMapToolMenuVisibilityAtom);
    const isShowTemplateTool = useAtomValue(templateToolMenuVisibility);

    const handleToolClick = (toolName: (typeof toolNames)[number]) => {
        if (isBuyProLockedTool(toolName)) {
            show(BuyProModal, {
                feature: toolName === "commentTool" ? "comments" : "presentation",
            });
            return;
        }

        const tool = canv.toolBox.TryGetToolByName(toolName);
        canv.toolBox.switchTo(tool);
    };

    return (
        <div className={cx(styles.toolBox, { [styles.toolBoxHidden]: !isShowToolBox })}>
            {isShowEmoteToolMenu && <EmoteMenu />}
            {isShowMindMapToolMenu && <MindMapMenu />}
            <div className={styles.toolBoxWrapper}>
                <VStack gap={2} className={styles.toolBoxWrapperContent}>
                    {toolNames.map((toolName) => {
                        const tool = canv.toolBox.TryGetToolByName(toolName);
                        const isBuyProLocked = isBuyProLockedTool(toolName);
                        const toolButton = (
                            // biome-ignore lint/a11y/noStaticElementInteractions: toolbox item click
                            <div
                                onClick={() => handleToolClick(toolName)}
                                className={cx(
                                    styles.toolBoxWrapperContentItem,
                                    tool.getToolClass(),
                                    {
                                        [styles.toolBoxWrapperContentItemSelected]:
                                            currentTool.name === toolName,
                                        [styles.toolBoxWrapperContentItemLocked]: isBuyProLocked,
                                    },
                                )}
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG from tools
                                dangerouslySetInnerHTML={{ __html: tool.getToolView() }}
                            />
                        );

                        return (
                            <div key={toolName} className={styles.toolWrapper}>
                                {toolName === "moreTool" && isShowMoreTool && <MoreWindow />}
                                {toolName === "stickerTool" && isShowStickerToolMenu && (
                                    <StickerMenu />
                                )}
                                {toolName === "arrowTool" && isShowArrowToolMenu && <ArrowMenu />}
                                {toolName === "mindMapTool" && isShowMindMapToolMenu && (
                                    <MindMapMenu />
                                )}
                                {toolName === "penTool" && isShowPenToolMenu && <PenMenu />}
                                {toolName === "templatesTool" && isShowTemplateTool && (
                                    <TemplatesWindow />
                                )}
                                {isBuyProLocked ? (
                                    toolButton
                                ) : (
                                    <Tooltip
                                        content={tool.getToolTipName()}
                                        hotkey={tool.getToolHotKey()}
                                        placement="right"
                                    >
                                        {toolButton}
                                    </Tooltip>
                                )}
                            </div>
                        );
                    })}
                </VStack>
            </div>
        </div>
    );
};
